import { Component, Input, OnInit, OnDestroy, ElementRef, ViewChild, OnChanges, SimpleChanges, AfterViewInit } from '@angular/core';
import * as d3 from 'd3';

export interface FunnelData {
  name: string;
  value: number; // The absolute value (e.g., 276)
  percentage: number; // The percentage value
}

interface SegmentSpec {
  width: number;
  height: number;
}

@Component({
  selector: 'app-funnel-chart-3d',
  template: '<div #funnelContainer></div>',
  styles: [`
    :host {
      display: block;
      width: 100%;
      height: 100%;
      overflow: hidden;
      box-sizing: border-box;
    }
    
    :host > div {
      overflow: hidden;
      width: 100%;
      height: 100%;
      box-sizing: border-box;
      position: relative;
    }
    
    .label-line {
      stroke: #BEBFBF;
      stroke-width: 1.73px;
      stroke-dasharray: 12.103017807006836, 12.103017807006836;
      fill: none;
    }

    .segment-value-text {
      fill: #FFFFFF;
      font-size: 16px;
      font-weight: 700;
      font-style: bold;
      text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.4);
      pointer-events: none;
    }

    .stage-label-text {
      font-family: Inter, sans-serif;
      fill: #000000;
      font-size: 14px;
      font-weight: 600;
      font-style: normal;
      line-height: 100%;
      letter-spacing: -0.7%;
      pointer-events: none;
    }
    
    .funnel-rim-path {
      /* Apply color for the rim */
      fill: #760DC6; 
      stroke: #760DC6; 
      stroke-width: 1px;
    }
  `]
})
export class FunnelChart3dComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  @ViewChild('funnelContainer', { static: true }) private funnelContainer!: ElementRef;
  
  @Input() data: FunnelData[] = [];
  @Input() width = 600;
  @Input() height = 400;

  private svg: any;
  private svgGroup: any;
  private margin = { top: 50, right: 150, bottom: 20, left: 150 };
  private actualWidth = 600;
  private actualHeight = 400;
  private resizeObserver?: ResizeObserver;
  private resizeTimeout?: any;
  private isUpdating = false;

  constructor(private elementRef: ElementRef) {} 
  
  // Figma colors
  private readonly colors = [
    '#760DC6', // Dark Purple
    '#AD6EDD', // Medium Purple
    '#ED338A', // Hot Pink
    '#F485B9'   // Light Pink
  ];

  // --- Figma RIM specification (New element) ---
  private readonly FigmaRimSpec: SegmentSpec = { width: 164.05, height: 22.98 };

  // --- Figma segment specifications (width/height in original pixels) ---
  private readonly FigmaSegmentSpecs: SegmentSpec[] = [
    { width: 158.57, height: 48.21 }, // S1: Top segment
    { width: 117.38, height: 49.25 }, // S2
    { width: 96.00, height: 48.73 }, // S3: Reduced bottom width of S2 for better funnel shape
    { width: 78.00, height: 52.58 }  // S4: Adjusted for smoother funnel progression
  ];
  // --- End Figma segment specifications ---


  ngOnInit(): void {
    this.updateContainerSize();
    this.updateMargins();
  }

  ngAfterViewInit(): void {
    this.setupResizeObserver();
    this.updateContainerSize();
    this.createFunnelChart();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['data'] && !changes['data'].firstChange) || 
        (changes['width'] && !changes['width'].firstChange) || 
        (changes['height'] && !changes['height'].firstChange)) {
      if (this.isUpdating) {
        return; // Prevent updates during resize observer callback
      }
      this.updateContainerSize();
      this.updateMargins();
      this.createFunnelChart();
    }
  }

  private setupResizeObserver(): void {
    if (typeof ResizeObserver !== 'undefined' && this.elementRef?.nativeElement) {
      this.resizeObserver = new ResizeObserver(() => {
        // Debounce resize events to prevent infinite loops
        if (this.resizeTimeout) {
          clearTimeout(this.resizeTimeout);
        }
        
        this.resizeTimeout = setTimeout(() => {
          if (this.isUpdating) {
            return; // Prevent recursive updates
          }
          
          const oldWidth = this.actualWidth;
          const oldHeight = this.actualHeight;
          
          this.updateContainerSize();
          
          // Only update if size actually changed
          if (oldWidth !== this.actualWidth || oldHeight !== this.actualHeight) {
            this.isUpdating = true;
            this.updateMargins();
            this.createFunnelChart();
            // Reset flag after a short delay
            setTimeout(() => {
              this.isUpdating = false;
            }, 100);
          }
        }, 150); // 150ms debounce
      });
      
      // Observe the parent container, not the host element itself
      const parentElement = this.elementRef.nativeElement.parentElement;
      if (parentElement) {
        this.resizeObserver.observe(parentElement);
      } else {
        // Fallback to host element if no parent
        this.resizeObserver.observe(this.elementRef.nativeElement);
      }
    }
  }

  private updateContainerSize(): void {
    if (!this.elementRef?.nativeElement) {
      // Fallback to input values
      this.actualWidth = this.width || 600;
      this.actualHeight = this.height || 400;
      return;
    }

    // Get the actual container element (host element)
    const containerElement = this.elementRef.nativeElement;
    
    if (containerElement) {
      const rect = containerElement.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        // Use actual container size, but ensure minimum dimensions
        // Round to avoid floating point precision issues that could cause loops
        const newWidth = Math.max(300, Math.floor(rect.width));
        const newHeight = Math.max(200, Math.floor(rect.height));
        
        // Only update if there's a meaningful change (at least 1px difference)
        if (Math.abs(newWidth - this.actualWidth) > 1 || Math.abs(newHeight - this.actualHeight) > 1) {
          this.actualWidth = newWidth;
          this.actualHeight = newHeight;
        }
      } else {
        // Fallback to input values or calculated defaults
        this.actualWidth = this.width || 600;
        this.actualHeight = this.height || 400;
      }
    } else {
      // Fallback to input values
      this.actualWidth = this.width || 600;
      this.actualHeight = this.height || 400;
    }
  }

  private updateMargins(): void {
    // Calculate responsive margins based on actual container size
    const containerWidth = this.actualWidth;
    let horizontalMargin: number;
    
    // Calculate margins as percentage of container width, but ensure labels fit
    // For smaller containers, use smaller margins
    if (containerWidth >= 1200) {
      horizontalMargin = Math.max(150, containerWidth * 0.25);
    } else if (containerWidth >= 768) {
      horizontalMargin = Math.max(100, containerWidth * 0.2);
    } else if (containerWidth >= 500) {
      horizontalMargin = Math.max(80, containerWidth * 0.15);
    } else {
      // For very small screens, use minimal margins but ensure some space for labels
      horizontalMargin = Math.max(60, containerWidth * 0.12);
    }
    
    // Ensure margins don't exceed container bounds
    const maxHorizontalMargin = (containerWidth - 200) / 2; // Leave at least 200px for chart
    horizontalMargin = Math.min(horizontalMargin, maxHorizontalMargin);
    
    this.margin = { 
      top: 50, 
      right: Math.max(60, horizontalMargin), 
      bottom: 20, 
      left: Math.max(60, horizontalMargin) 
    }; 
  }
  
  ngOnDestroy(): void {
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.svg) {
      this.svg.remove();
    }
  }

  private addMarkers(): void {
    const defs = this.svg.append('defs');
    
    const circleMarker = defs.append('marker')
      .attr('id', 'circleMarker')
      .attr('viewBox', '0 0 10 10')
      .attr('refX', 5)
      .attr('refY', 5)
      .attr('markerWidth', 10)
      .attr('markerHeight', 10)
      .attr('orient', 'auto-start-reverse');

    circleMarker.append('circle')
      .attr('cx', 5)
      .attr('cy', 5)
      .attr('r', 4)
      .attr('fill', '#FFFFFF') 
      .attr('stroke', '#000000') 
      .attr('stroke-width', '1px');
  }

  private createFunnelRim(cx: number, r: number, h: number): void {
    const rimColor = '#760DC6';

    // Draw the elliptical rim, centered vertically at Y = h
    this.svgGroup.append('path')
        .attr('class', 'funnel-rim-path')
        .attr('d', this.createFunnelRimPath(cx, r, h))
        .style('fill', rimColor);
  }

  private createFunnelRimPath(cx: number, r: number, h: number): string {
    // Draws a closed ellipse path (rx=r, ry=h). The ellipse is drawn centered at (cx, h).
    return `M ${cx - r}, 0 A ${r}, ${h} 0 1,0 ${cx + r}, 0 A ${r}, ${h} 0 1,0 ${cx - r}, 0 Z`;
  }

  private createFunnelChart(): void {
    d3.select(this.funnelContainer.nativeElement).selectAll('*').remove();

    if (!this.data || this.data.length === 0) {
      console.warn("Funnel data is empty. Cannot render chart.");
      return; 
    }
    
    const specs = this.FigmaSegmentSpecs.slice(0, this.data.length);
    const totalSegments = specs.length;

    if (totalSegments === 0) return;

    // Use actual container dimensions
    const svgWidth = this.actualWidth;
    const svgHeight = this.actualHeight;
    
    // Calculate maximum label width needed to determine required margin space
    let maxLabelWidth = 0;
    this.data.forEach((d) => {
      const labelWidth = this.measureTextWidth(d.name, 'Inter', '600', '14px');
      maxLabelWidth = Math.max(maxLabelWidth, labelWidth);
    });
    
    // Ensure margins are large enough for labels
    // Labels need: line extension (30px) + text offset (40px) + label width + padding (20px)
    const requiredLabelSpace = Math.max(
      maxLabelWidth + 90, // 90px for line extension + text offset + padding
      this.margin.left, // Use existing margin if larger
      this.margin.right
    );
    
    // Update margins to ensure labels fit, but don't exceed container
    const maxHorizontalMargin = (svgWidth - 200) / 2; // Leave at least 200px for chart
    const adjustedHorizontalMargin = Math.min(requiredLabelSpace, maxHorizontalMargin);
    this.margin.left = Math.max(60, adjustedHorizontalMargin);
    this.margin.right = Math.max(60, adjustedHorizontalMargin);
    
    // Calculate chart area with adjusted margins
    const chartWidth = svgWidth - this.margin.left - this.margin.right;
    const chartHeight = (svgHeight - this.margin.top - this.margin.bottom) * 0.9;
    const centerX = chartWidth / 2;
    
    if (chartWidth <= 0 || chartHeight <= 0) {
      console.error("Chart area is zero or negative. Adjust width/height or margins.");
      return;
    }

    // ViewBox matches SVG dimensions to include all content
    const viewBoxWidth = svgWidth; 
    const viewBoxHeight = svgHeight; 
    const viewBoxX = 0; 
    const viewBoxY = 0; 

    this.svg = d3.select(this.funnelContainer.nativeElement)
      .selectAll('svg')
      .data([1])
      .join('svg')
      .attr('width', svgWidth)
      .attr('height', svgHeight)
      .attr('viewBox', `${viewBoxX} ${viewBoxY} ${viewBoxWidth} ${viewBoxHeight}`)
      .attr('preserveAspectRatio', 'xMidYMid meet')
      .style('overflow', 'hidden') // Keep hidden to contain everything within SVG
      .style('max-width', '100%')
      .style('max-height', '100%')
      .style('display', 'block');
    
    // Remove existing group if it exists
    this.svg.selectAll('g').remove();
    
    // Create the main group for chart elements
    this.svgGroup = this.svg.append('g')
      .attr('transform', `translate(${this.margin.left}, ${this.margin.top})`);
    
    this.addMarkers();
    
    const GAP_SIZE = 3; // Reduced gap size for closer segments
    const ALIGNMENT_OFFSET = 1.0; // Reduced alignment offset for more seamless transitions
    const MIN_RADIUS_PX = 40; // Minimum visual radius if value is near zero
    
    // --- DATA-DRIVEN CALCULATION SETUP ---
    const maxValue = d3.max(this.data, d => d.value) || 1;
    
    // 1. Calculate Total Figma Dimensions
    const targetTotalHeight = specs.reduce((sum, spec) => sum + spec.height, 0);

    const totalGapHeight = (totalSegments > 0 ? totalSegments - 1 : 0) * GAP_SIZE;
    
    // 2. Calculate Scale Factors
    // Reduce scale to ensure labels fit - use 90% of available space for funnel
    const effectiveChartHeight = chartHeight - totalGapHeight;
    const heightScaleFactor = (effectiveChartHeight / targetTotalHeight) * 0.90; // Reduce by 10%
    const targetMaxWidth = specs[0].width;
    
    // Reduce target width to leave more room for labels
    const TARGET_TOP_WIDTH = Math.min(250, chartWidth * 0.70); // Use 70% of chart width max
    const widthScaleFactor = Math.min(TARGET_TOP_WIDTH / targetMaxWidth, (chartWidth * 0.70) / targetMaxWidth);
    
    // Calculate max funnel radius based on first segment's width (maintains structure)
    const maxFunnelRadius = (specs[0].width * widthScaleFactor) / 2;
    // --- END DATA-DRIVEN CALCULATION SETUP ---

    // --- RIM Calculation and Drawing ---
    const rimSpec = this.FigmaRimSpec;
    const rimHeight = rimSpec.height * heightScaleFactor;
    const rimWidth = rimSpec.width * widthScaleFactor;
    const rimRadius = rimWidth / 2;
    
    // Draw Rim (uses half height for ry)
    const rimVerticalRadius = rimHeight / 2;
    this.createFunnelRim(centerX, rimRadius, rimVerticalRadius); 

    // --- CRITICAL VERTICAL SHIFT FIX ---
    // Stack starts overlapping rim's bottom edge to ensure they touch
    // Increased overlap so rim overlaps onto first segment for perfect connection
    let currentY = rimHeight - rimVerticalRadius * 1.3; 
    // --- END RIM Logic ---


    let prevBottomRadius: number | null = null;
    
    this.data.forEach((d, i) => {
      if (i >= totalSegments) return; 

      const spec = specs[i];

      const segmentHeight = spec.height * heightScaleFactor;

      // --- CRITICAL: DATA-DRIVEN RADIUS CALCULATION ---
      // r_top is based on the current segment's value (d.value) while maintaining structure
      // Blend the value-based radius with the structure-based radius to maintain design
      const baseWidth = spec.width * widthScaleFactor;
      const baseRadius = baseWidth / 2;
      const valueProportion = d.value / maxValue;
      
      // Increase minimum size for segments with value 0
      const minRadiusMultiplier = d.value === 0 ? 0.8 : 0.5; // Slightly larger minimum for value 0
      
      // Blend: 70% value-based, 30% structure-based to maintain funnel shape
      const r_top_raw = Math.max(
        (valueProportion * maxFunnelRadius * 0.7) + (baseRadius * 0.3),
        MIN_RADIUS_PX * widthScaleFactor * minRadiusMultiplier // Ensure a minimum size
      );
      
      let r_bottom_raw: number;
      if (i < totalSegments - 1) {
          // r_bottom is based on the next segment's value (data[i+1].value)
          const nextValue = this.data[i + 1].value;
          const nextValueProportion = nextValue / maxValue;
          const nextBaseWidth = specs[i + 1].width * widthScaleFactor;
          const nextBaseRadius = nextBaseWidth / 2;
          // Increase minimum size if next segment has value 0
          const nextMinRadiusMultiplier = nextValue === 0 ? 0.8 : 0.5;
          r_bottom_raw = Math.max(
            (nextValueProportion * maxFunnelRadius * 0.7) + (nextBaseRadius * 0.3),
            MIN_RADIUS_PX * widthScaleFactor * nextMinRadiusMultiplier
          );
      } else {
          // Last segment: scale down based on value but maintain minimum
          const lastValueProportion = d.value / maxValue;
          r_bottom_raw = Math.max(
            (lastValueProportion * maxFunnelRadius * 0.4) + (baseRadius * 0.2),
            MIN_RADIUS_PX * widthScaleFactor * minRadiusMultiplier
          );
      }
      
      // Determine segment type
      const isTopSegment = i === 0; 
      const isBottomSegment = i === totalSegments - 1; 
      // Even positions (0, 2, 4...) = right, Odd positions (1, 3, 5...) = left
      const isLeftLabel = i % 2 === 1;
      
      // --- CRITICAL ALIGNMENT STEP (Horizontal Overlap for Smooth Sides) ---
      // Make top of lower segment slightly narrower than bottom of segment above for seamless flow
      let r_top: number;
      if (i > 0 && prevBottomRadius !== null) {
        // Reduce top radius slightly to create seamless funnel appearance
        r_top = prevBottomRadius - (ALIGNMENT_OFFSET * widthScaleFactor);
      } else {
        r_top = r_top_raw;
      }
      
      // For last segment, make it a true cylinder (r_bottom = r_top)
      let r_bottom: number;
      if (isBottomSegment) {
        r_bottom = r_top; // Cylinder: same radius top and bottom
      } else {
        // Narrow the bottom to allow next segment to smoothly overlap
        r_bottom = r_bottom_raw - (ALIGNMENT_OFFSET * widthScaleFactor);
      }
      
      // Store bottom radius for next iteration (use raw value for next segment's top calculation)
      prevBottomRadius = r_bottom_raw; 

      const segmentGroup = this.svgGroup.append('g')
        .attr('transform', `translate(0, ${currentY})`)
        .attr('class', `funnel-segment-${i}`);

      // 1. Segment Body 
      segmentGroup.append('path')
        .attr('d', this.createBodyPath(centerX, r_top, r_bottom, segmentHeight, isTopSegment, isBottomSegment))
        .style('fill', this.colors[i % this.colors.length]) 
        .style('stroke', 'none'); 
      
      // 2. Central Value Text
      segmentGroup.append('text')
        .attr('class', 'segment-value-text')
        .attr('x', centerX)
        .attr('y', segmentHeight / 2 + 8)
        .attr('text-anchor', 'middle')
        .style('fill', '#FFFFFF')
        .style('font-weight', '700')
        .style('font-style', 'bold')
        .style('font-size', '16px')
        .text(d.value.toString());

      // 3. Label Connector Line and Endpoint
      const labelY = segmentHeight / 2;
      let labelXAnchor: string;
      let lineStartX: number;
      let lineEndX: number;
      let labelTextX: number;
      
      // Calculate maximum available width for label based on container bounds
      const containerPadding = 15; // Padding from container edges
      const minLabelWidth = 50; // Minimum width to ensure text wrapping works
      
      // Use raw radius for label positioning to align with actual segment width
      // Responsive label offset and positioning based on container size
      const containerWidth = this.actualWidth;
      const baseOffset = containerWidth >= 768 ? 20 : 15;
      const labelOffset = r_top_raw + baseOffset; 
      // Adjust line extension and text offset to ensure labels fit within margins
      const maxAvailableSpace = isLeftLabel 
        ? this.margin.left - containerPadding  // Space available on left side (with padding)
        : this.margin.right - containerPadding; // Space available on right side (with padding)
      const lineExtension = Math.min(containerWidth >= 768 ? 25 : 15, Math.max(15, maxAvailableSpace * 0.3));
      const textOffset = Math.min(containerWidth >= 768 ? 35 : 25, Math.max(20, maxAvailableSpace * 0.5));
      
      if (isLeftLabel) {
        labelXAnchor = 'end';
        // Start from the left edge of the segment - use r_top to match actual path edge
        lineStartX = centerX - r_top; 
        lineEndX = centerX - labelOffset - lineExtension; 
        labelTextX = centerX - labelOffset - textOffset;
        
        // Constrain labelTextX to stay within SVG bounds (in chart coordinates, left edge is at -margin.left)
        // Ensure label doesn't go beyond left margin
        const minLabelX = -this.margin.left + containerPadding;
        labelTextX = Math.max(minLabelX, labelTextX);
        // Adjust lineEndX to match
        lineEndX = Math.max(lineEndX, labelTextX - 10);
      } else {
        labelXAnchor = 'start';
        // Start from the right edge of the segment - use r_top to match actual path edge
        lineStartX = centerX + r_top; 
        lineEndX = centerX + labelOffset + lineExtension; 
        labelTextX = centerX + labelOffset + textOffset;
        
        // Constrain labelTextX to stay within SVG bounds (in chart coordinates)
        // Right edge in chart coords = chartWidth + margin.right = svgWidth - margin.left
        const maxLabelX = chartWidth + this.margin.right - containerPadding;
        labelTextX = Math.min(maxLabelX, labelTextX);
        // Adjust lineEndX to match
        lineEndX = Math.min(lineEndX, labelTextX + 10);
      }
      
      segmentGroup.append('path')
        .attr('class', 'label-line')
        .attr('d', `M ${lineStartX},${labelY} L ${lineEndX},${labelY}`)
        // For left labels: marker at end (label side), for right labels: marker at end (label side)
        .attr('marker-end', 'url(#circleMarker)')
        .attr('marker-start', null)
        .style('fill', 'none')
        .style('stroke', '#BEBFBF')
        .style('stroke-width', '1.73px')
        .style('stroke-dasharray', '12.103017807006836, 12.103017807006836');
      
      // Use actual container width for label positioning
      const actualContainerWidth = this.actualWidth;
      
      // labelTextX is in chart coordinates (after margin.left transform)
      // The SVG group is translated by margin.left, so:
      // - In SVG coordinates: the group starts at X = margin.left
      // - In group (chart) coordinates: X=0 corresponds to SVG X=margin.left
      // - The right edge of SVG (X=actualContainerWidth) in chart coords = actualContainerWidth - margin.left
      const containerRightEdgeInChartCoords = actualContainerWidth - this.margin.left;
      
      let maxLabelWidth: number;
      if (isLeftLabel) {
        // For left labels: width from labelTextX to left edge of container (in chart coordinates, left edge is at 0)
        // Ensure label doesn't go beyond the left margin
        maxLabelWidth = Math.max(minLabelWidth, labelTextX - containerPadding);
      } else {
        // For right labels: width from labelTextX to right edge of container
        // labelTextX is in chart coordinates, container right edge in chart coordinates
        const availableWidth = containerRightEdgeInChartCoords - labelTextX - containerPadding;
        maxLabelWidth = Math.max(minLabelWidth, availableWidth);
      }
      
      // Create multi-line text for labels with proper width-based wrapping
      const labelLines = this.wrapTextByWidth(d.name, maxLabelWidth, 'Inter', '600', '14px');
      const lineHeight = 20; // Line height for multi-line text
      const textElement = segmentGroup.append('text')
        .attr('class', 'stage-label-text')
        .attr('x', labelTextX)
        .attr('y', labelY + 6 - ((labelLines.length - 1) * lineHeight / 2)) // Center vertically for multi-line
        .attr('text-anchor', labelXAnchor)
        .style('font-family', 'Inter, sans-serif')
        .style('font-size', '14px')
        .style('font-weight', '600')
        .style('letter-spacing', '-0.7%');
      
      // Add each line as a tspan
      labelLines.forEach((line, index) => {
        textElement.append('tspan')
          .attr('x', labelTextX)
          .attr('dy', index === 0 ? 0 : lineHeight)
          .text(line);
      }); 

      currentY += segmentHeight; 
      
      if (i < totalSegments - 1) {
          currentY += GAP_SIZE;
      }
    });
  }

  // Path functions (Updated to remove rimCurveDepth parameter and use fixed depths)
  private createBodyPath(cx: number, r_top: number, r_bottom: number, h: number, isTop: boolean, isBottom: boolean): string {
    
    let path = `M ${cx - r_top}, 0 `;

    // CURVE DEPTH ADJUSTMENT (Standard segment depth)
    // Reduced curvature for top segment
    const topCurveDepth = isTop ? 18 : 16; 
    const bottomCurveDepth = 18; // Increased curvature for bottom edges of all segments 
    
    // --- SMOOTH CURVATURE FOR FUNNEL ALIGNMENT ---
    // Calculate control points to ensure smooth, continuous curves between segments
    const radiusDifference = r_top - r_bottom;
    // Use consistent curve offset for smooth transitions - reduced for more seamless alignment
    const curveOffset = radiusDifference * 0.15; 

    // 1. TOP EDGE (Universal Convex Curve: bows DOWNWARD)
    path += `Q ${cx}, ${topCurveDepth}, ${cx + r_top}, 0 `;
    

    // 2. RIGHT SIDE
    if (isBottom) {
      // Last segment: straight vertical line (cylinder)
      path += `L ${cx + r_bottom}, ${h} `;
    } else {
      // Smooth concave curve aligned for funnel continuity
      const r_avg_right = (r_top + r_bottom) / 2;
      const cpX_right = cx + r_avg_right - curveOffset; 
      const cpY_right = h * 0.5; // Consistent midpoint
      path += `Q ${cpX_right}, ${cpY_right}, ${cx + r_bottom}, ${h} `; 
    }
    
    // 3. BOTTOM EDGE (Universal Convex Curve: bows DOWNWARD)
    path += `Q ${cx}, ${h + bottomCurveDepth}, ${cx - r_bottom}, ${h} `;
    

    // 4. LEFT SIDE
    if (isBottom) {
      // Last segment: straight vertical line (cylinder)
      path += `L ${cx - r_top}, 0 `;
    } else {
      // Smooth concave curve aligned for funnel continuity
      const r_avg_left = (r_top + r_bottom) / 2;
      const cpX_left = cx - r_avg_left + curveOffset;
      const cpY_left = h * 0.5; // Consistent midpoint
      path += `Q ${cpX_left}, ${cpY_left}, ${cx - r_top}, 0 `;
    }
    
    return path;
  }
  
  private createRimPath(cx: number, cy: number, r: number, p: number): string {
    return "";
  }

  private wrapText(text: string, maxLength: number = 15): string[] {
    // Split text into words and create lines that don't exceed maxLength
    const words = text.split(' ');
    const lines: string[] = [];
    let currentLine = '';

    words.forEach((word, index) => {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      if (testLine.length <= maxLength || !currentLine) {
        currentLine = testLine;
      } else {
        lines.push(currentLine);
        currentLine = word;
      }
      
      // Push last line
      if (index === words.length - 1) {
        lines.push(currentLine);
      }
    });

    return lines.length > 0 ? lines : [text];
  }

  private measureTextWidth(text: string, fontFamily: string, fontWeight: string, fontSize: string): number {
    // Create a temporary canvas element to measure text width
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) return text.length * 10; // Fallback estimate
    
    context.font = `${fontWeight} ${fontSize} ${fontFamily}`;
    return context.measureText(text).width;
  }

  private wrapTextByWidth(text: string, maxWidth: number, fontFamily: string, fontWeight: string, fontSize: string): string[] {
    if (maxWidth <= 0) {
      return [text]; // No space available, return as-is
    }

    const words = text.split(' ');
    const lines: string[] = [];
    let currentLine = '';

    words.forEach((word, index) => {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      const testWidth = this.measureTextWidth(testLine, fontFamily, fontWeight, fontSize);
      
      if (testWidth <= maxWidth || !currentLine) {
        // If it fits or it's the first word, add it to current line
        currentLine = testLine;
      } else {
        // Current line is full, push it and start a new line with the word
        lines.push(currentLine);
        currentLine = word;
      }
      
      // Push last line
      if (index === words.length - 1) {
        lines.push(currentLine);
      }
    });

    return lines.length > 0 ? lines : [text];
  }

  public updateData(newData: FunnelData[]): void {
    this.data = newData;
    this.createFunnelChart();
  }
}